import React from 'react';

import axios from 'helpers/axios';
import config from 'helpers/config';
import Tenant from 'models/TenantModel';
import TenantSettings from 'models/TenantSettingsModel';
import Checkbox from 'components/common/Checkbox';
import FormModes from 'helpers/FormModes';
import { Save, Edit, X, Info, UserCheck, Mail, Phone, MapPin, Calendar, Users } from 'lucide-react';

interface Props {}

interface State {
    tenant: Tenant | null;
    companySettings: TenantSettings | null;
    formState: FormModes;
    beforeEdit: any;
}

class ROPostingRulesSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            tenant: null,
            companySettings: null,
            formState: FormModes.Preview,
            beforeEdit: {},
        };

        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        this.getCompanySettings();
    }

    getCompanySettings() {
        axios
            .get(`${config.apiUrl}/tenant/settings`)
            .then((res) => this.setState({ tenant: res.data, companySettings: res.data.tenantSettings }));
    }

    save() {
        const { companySettings } = this.state;
        axios
            .put(`${config.apiUrl}/tenant/`, { ...companySettings })
            .then((res) =>
                this.setState({ tenant: res.data, companySettings, formState: FormModes.Preview, beforeEdit: {} }),
            );
    }
    handleChange(e: React.FormEvent<any>) {
        const { name, value } = e.currentTarget;
        const { companySettings } = this.state;
        if (companySettings) {
            (companySettings as any)[name] = value;
            this.setState({ companySettings });
        }
    }
    render() {
        const { companySettings, formState } = this.state;

        const fieldConfigs = [
            {
                name: 'isCustomerEmailRequired',
                label: 'Email Address',
                icon: Mail,
                description: 'Require customers to provide their email address for communication and receipts',
                value: Boolean(companySettings?.isCustomerEmailRequired),
            },
            {
                name: 'isCustomerPhoneRequired',
                label: 'Phone Number',
                icon: Phone,
                description: 'Require customers to provide their phone number for contact and notifications',
                value: Boolean(companySettings?.isCustomerPhoneRequired),
            },
            {
                name: 'isCustomerAddressRequired',
                label: 'Physical Address',
                icon: MapPin,
                description: 'Require customers to provide their physical address for service and billing',
                value: Boolean(companySettings?.isCustomerAddressRequired),
            },
            {
                name: 'isCustomerBirthdateRequired',
                label: 'Birth Date',
                icon: Calendar,
                description: 'Require customers to provide their birth date for identification and promotions',
                value: Boolean(companySettings?.isCustomerBirthdateRequired),
            },
            {
                name: 'isCustomerSourceRequired',
                label: 'Referral Source',
                icon: Users,
                description: 'Require customers to specify how they found your business for marketing analytics',
                value: Boolean(companySettings?.isCustomerSourceRequired),
            },
        ];

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Customer Information</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Required Customer Information</h2>
                            <p className="text-gray-600 mt-1">
                                Configure which customer information fields are mandatory when creating new customer
                                profiles.
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {formState === FormModes.Preview && (
                                <button
                                    onClick={() => {
                                        this.setState({
                                            formState: FormModes.Edit,
                                            beforeEdit: { ...this.state.companySettings },
                                        });
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                >
                                    <Edit className="h-4 w-4" />
                                    <span>Edit Settings</span>
                                </button>
                            )}

                            {formState === FormModes.Edit && (
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => {
                                            this.setState({
                                                companySettings: { ...this.state.beforeEdit },
                                                formState: FormModes.Preview,
                                                beforeEdit: {},
                                            });
                                        }}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                        <span>Cancel</span>
                                    </button>

                                    <button
                                        onClick={() => {
                                            this.save();
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <Save className="h-4 w-4" />
                                        <span>Save Changes</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Status Banner */}
                    <div className="mb-8">
                        <div
                            className={`rounded-lg border p-4 ${
                                formState === FormModes.Edit ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                            }`}
                        >
                            <div className="flex items-center">
                                <div
                                    className={`p-2 rounded-lg ${
                                        formState === FormModes.Edit ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}
                                >
                                    <UserCheck
                                        className={`h-5 w-5 ${
                                            formState === FormModes.Edit ? 'text-blue-600' : 'text-gray-600'
                                        }`}
                                    />
                                </div>
                                <div className="ml-4">
                                    <h3
                                        className={`text-lg font-semibold ${
                                            formState === FormModes.Edit ? 'text-blue-900' : 'text-gray-900'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Editing Customer Requirements'
                                            : 'Customer Information Requirements'}
                                    </h3>
                                    <p
                                        className={`text-sm ${
                                            formState === FormModes.Edit ? 'text-blue-700' : 'text-gray-600'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Make your changes below and click Save Changes to apply them.'
                                            : 'Currently configured required fields for new customer registration.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer Information Fields */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Customer Information Fields</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Select which fields are required when creating new customer profiles
                            </p>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {fieldConfigs.map((field, _) => {
                                const IconComponent = field.icon;
                                return (
                                    <div
                                        key={field.name}
                                        className={`p-6 ${formState === FormModes.Preview ? 'opacity-75' : ''}`}
                                    >
                                        <div className="flex items-start">
                                            <div className="flex items-center h-5">
                                                <Checkbox
                                                    name={field.name}
                                                    value={field.value}
                                                    onChange={
                                                        formState === FormModes.Preview ? undefined : this.handleChange
                                                    }
                                                    size={5}
                                                />
                                            </div>
                                            <div className="ml-4 flex-1">
                                                <div className="flex items-center">
                                                    <IconComponent className="h-5 w-5 text-gray-400 mr-3" />
                                                    <label
                                                        className="text-lg font-medium text-gray-900 cursor-pointer"
                                                        htmlFor={field.name}
                                                    >
                                                        {field.label}
                                                    </label>
                                                    {field.value && (
                                                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                            Required
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-2 text-sm text-gray-600">{field.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">How Required Fields Work</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Required fields must be filled in before a customer profile can be saved
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Optional fields can be left empty and filled in later if needed
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Email addresses are recommended for sending receipts and communication
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Phone numbers help with appointment reminders and urgent updates
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Changes to these settings apply to all new customer registrations going forward
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ROPostingRulesSettings;
